import 'lib/polyfills/resizeObserverPolyfill';

/* eslint-disable import/first */
import Modal from 'react-modal';
import { sendConfig, sendUser } from 'lib/devtoolsHelpers';
import { useTimeoutFn } from 'react-use';
import { useDispatch } from 'react-redux';
import { AppProps } from 'next/app';
import config from 'lib/config';
import makeStore from 'redux-implementations/makeStore';
import i18next, { LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP, Language } from 'lib/i18next';
import { createWrapper } from 'next-redux-wrapper';
import Head from 'next/head';
import googleAnalytics from 'lib/GoogleAnalytics';
import FacebookPixel from 'lib/FacebookPixel';
import { dontIncrementBounceRate } from 'lib/Tracking';
import eventLogger from 'lib/EventLogger';
import useInitializeSnowPlow from 'lib/hooks/useInitializeSnowPlow';
import { I18nextProvider } from 'react-i18next';
import { addEventListener } from 'lib/GoogleAds';
import { slotRenderEnded } from 'redux-implementations/googleAds';
import { useEffect } from 'react';
import { signedIn, signedOut } from 'redux-implementations/session';
import { onAuthStateChanged } from 'lib/Auth';
import favicon from 'public/static/images/favicon.ico';
import { ValidationProvider } from 'lib/hooks/useValidation';
import initializeFirebaseClient from 'lib/firebaseInitializers/client';
import { useRouter } from 'next/router';
import LoadingState from 'components/LoadingState';
import useGoogleOneTap from 'lib/hooks/useGoogleOneTap';
import AppScripts from 'lib/AppScripts';
// eslint-disable-next-line @next/next/no-script-in-document
import Script from 'next/script';

// We use a custom bootstrap build, which is hard to import
// from bootstrap's npm package in webpack
// due to global variable transpilation
import 'public/static/styles/bootstrap.css';
import 'react-responsive-carousel/lib/styles/carousel.min.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// eslint-disable-next-line import/no-unresolved
import { captureException } from '@sentry/nextjs';
import EventLoggerContext from 'contexts/EventLoggerContext';
import useLoadingState from 'lib/hooks/useLoadingState';
import PageViewEvent from 'lib/Tracking/PageViewEvent';
import ConfigContext from 'contexts/ConfigContext';
import logWebVitals from 'lib/logWebVitals';
import { fetchAutolistUser } from 'redux-implementations/user';
import useQuotaLimitErrorHandling from 'lib/hooks/useQuotaLimitErrorHandling';

try {
  initializeFirebaseClient();
} catch (e) {
  captureException(e);
}

config.activateRemoteConfig().catch(captureException);

Modal.setAppElement('body');

interface Props extends AppProps {
  pageProps: any;
}

function logPageViewEvent(path: string): void {
  eventLogger.log(new PageViewEvent(path));
}

function trackPageview(path = document.location.pathname): void {
  googleAnalytics.pageView();
  FacebookPixel.trackPageView();
  logPageViewEvent(path);
}

function useUpdateHtmlLang(): void {
  const setLang = (): void => {
    window.document.documentElement.lang = LANGUAGE_TO_LANGUAGE_WITH_COUNTRY_MAP.get(
      i18next.language as Language
    ) as string;
  };

  useEffect(() => {
    setLang();
    i18next.on('languageChanged', setLang);

    return (): void => {
      i18next.off('languageChanged', setLang);
    };
  });
}

export function MyApp({ Component, pageProps }: Props): JSX.Element {
  useQuotaLimitErrorHandling();

  const dispatch = useDispatch();
  const { asPath, locale } = useRouter();
  const isVdp = asPath?.includes('/noindex/listings');

  if (locale) i18next.changeLanguage(locale);

  useGoogleOneTap();

  useEffect(logWebVitals);

  useEffect(() => {
    sendConfig(config);
  });

  useUpdateHtmlLang();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged((firebaseUser) => {
      if (firebaseUser) {
        sendUser(firebaseUser);
        dispatch(signedIn(firebaseUser));
        dispatch(fetchAutolistUser());
      } else {
        dispatch(signedOut());
      }
    });

    return unsubscribe;
  }, [dispatch]);

  const { loading } = useLoadingState();

  useEffect(() => {
    addEventListener('slotRenderEnded', (event: googletag.events.Event) => {
      dispatch(
        slotRenderEnded({
          elementId: event.slot.getSlotElementId(),
          isEmpty: (event as any).isEmpty,
          isSuggestive: event.slot.getTargeting('slot').includes('suggestive'),
        })
      );
    });
  });

  useEffect(() => {
    eventLogger.touchSession();
  }, []);

  useInitializeSnowPlow();

  useEffect(() => {
    trackPageview();
  }, []);

  useTimeoutFn(dontIncrementBounceRate, 15000);

  // Not necessary to use ReactRedux Consumer, since `useDispatch` etc will handle that for us
  return (
    <I18nextProvider i18n={i18next}>
      <ConfigContext.Provider
        value={{
          configs: config.toJSON(),
          isReady: config.isReady,
        }}
      >
        <ValidationProvider>
          <EventLoggerContext.Provider value={eventLogger}>
            <Head>
              {!isVdp && (
                <meta
                  name="apple-itunes-app"
                  content={`app-id=1058636900, app-argument=${asPath}`}
                />
              )}
              <link rel="shortcut icon" href={favicon} />
            </Head>
            <Script
              src="https://polyfill.io/v3/polyfill.min.js?features=IntersectionObserver%2CObject.fromEntries"
              strategy="beforeInteractive"
            />
            <AppScripts />
            {loading ? <LoadingState /> : <Component {...pageProps} />}
          </EventLoggerContext.Provider>
        </ValidationProvider>
      </ConfigContext.Provider>
    </I18nextProvider>
  );
}

const wrapper = createWrapper(makeStore, { debug: false });

export default wrapper.withRedux(MyApp);
